import { Component, Inject, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TelnyxWebRTCService } from '../../services/telnyx-webrtc.service';
import { LeadAlertService } from '../../services/lead-alert.service';
import { CallAudioService } from '../../services/call-audio.service';
import { RingtoneConfigService } from '../../services/ringtone-config.service';
import { CallFacadeService } from '../../services/call';
import { Subject, takeUntil, interval, firstValueFrom } from 'rxjs';
import { TelnyxCall } from '@telnyx/webrtc';

export interface LeadAlertInfo {
  id: number;
  name: string;
  phone: string;
  email?: string;
  status?: string;
  campusId?: number;
  campusName?: string;
  programId?: number;
  programName?: string;
  ownerId?: number;
  ownerName?: string;
}

export interface LeadAlertCallData {
  call: TelnyxCall;
  leadInfo: LeadAlertInfo;
  sessionId: string;
  phoneNumber: string;
}

@Component({
  selector: 'app-lead-alert-call',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './lead-alert-call.component.html',
  styleUrls: ['./lead-alert-call.component.scss']
})
export class LeadAlertCallComponent implements OnInit, OnDestroy {
  // Cleanup
  private destroy$ = new Subject<void>();

  // Injected services
  private telnyxService = inject(TelnyxWebRTCService);
  private leadAlertService = inject(LeadAlertService);
  private audioService = inject(CallAudioService);
  private ringtoneConfig = inject(RingtoneConfigService);
  private calls = inject(CallFacadeService);
  private dialogRef = inject(MatDialogRef<LeadAlertCallComponent>);

  // State signals
  leadInfo = signal<LeadAlertInfo | null>(null);
  callPhase = signal<'ringing' | 'accepted' | 'connecting' | 'declined'>('ringing');
  isProcessing = signal(false);
  ringDuration = signal(0);
  statusMessage = signal('Incoming lead alert...');

  // Call reference
  private call: TelnyxCall | null = null;
  private sessionId: string | null = null;

  // Timer references for cleanup
  private autoDismissTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LeadAlertCallData
  ) {
    // Configure dialog behavior
    this.dialogRef.disableClose = true;

    // Store call and session data
    this.call = data.call;
    this.sessionId = data.sessionId;
    this.leadInfo.set(data.leadInfo);

    // Auto-dismiss after 45 seconds if no action taken
    this.autoDismissTimer = setTimeout(() => {
      if (this.callPhase() === 'ringing') {
        this.statusMessage.set('Alert timed out');
        setTimeout(() => this.dismiss('timeout'), 2000);
      }
    }, 45000);
  }

  ngOnInit(): void {
    console.log('🔔 [LeadAlertCall] Component initialized:', {
      sessionId: this.sessionId,
      leadInfo: this.leadInfo(),
      callId: this.call?.id
    });

    this.startRingTimer();
    this.startRingtone();
    this.setupCallStateListener();
  }

  ngOnDestroy(): void {
    console.log('🔔 [LeadAlertCall] Component destroyed');

    // Clear auto-dismiss timer
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.stopRingtone();
  }

  /**
   * Answer and Accept the lead alert in one action.
   * For WebRTC, answering = accepting since the agent sees all lead info visually.
   * No need for TTS/DTMF flow.
   */
  async answerAlert(): Promise<void> {
    if (this.isProcessing()) return;

    console.log('🔔 [LeadAlertCall] Answering and accepting lead alert');

    try {
      this.isProcessing.set(true);
      this.callPhase.set('accepted');
      this.statusMessage.set('Accepting lead...');
      this.stopRingtone();

      // Step 1: Answer the WebRTC call
      const answerSuccess = await this.calls.answerCall();

      if (!answerSuccess) {
        throw new Error('Failed to answer WebRTC call');
      }

      // Step 2: Immediately call accept API (auto-accept for WebRTC)
      if (!this.sessionId) {
        throw new Error('Session ID not available');
      }

      // Get call control ID from the current call
      const currentCall = this.telnyxService.currentCall();
      const callControlId = currentCall?.options?.telnyxCallControlId ||
                           (this.data.call as any)?.telnyxCallControlId ||
                           this.call?.id || '';

      console.log('🔔 [LeadAlertCall] Auto-accepting via API:', {
        sessionId: this.sessionId,
        callControlId
      });

      const response = await firstValueFrom(
        this.leadAlertService.acceptLeadAlert(this.sessionId, callControlId)
      );

      console.log('🔔 [LeadAlertCall] Accept response:', response);

      if (response.isWinner) {
        this.callPhase.set('connecting');
        this.statusMessage.set('Connecting to lead...');
        // Modal will close when call bridges (handled by setupCallStateListener)
      } else {
        // Another agent accepted first
        this.statusMessage.set('Another agent accepted first');
        this.callPhase.set('declined');
        setTimeout(() => this.dismiss('lost_race'), 2000);
      }

    } catch (error: any) {
      console.error('[LeadAlertCall] Answer/Accept error:', error);
      this.statusMessage.set('Failed to accept lead');
      this.callPhase.set('ringing');
      this.isProcessing.set(false);
      this.startRingtone();
    }
  }

  /**
   * Accept the lead - call API to notify backend
   */
  async acceptLead(): Promise<void> {
    if (this.isProcessing()) return;

    console.log('🔔 [LeadAlertCall] Accepting lead');

    this.isProcessing.set(true);
    this.callPhase.set('accepted');
    this.statusMessage.set('Accepting lead...');

    try {
      const callControlId = this.call?.options?.telnyxCallControlId || '';

      if (!this.sessionId) {
        throw new Error('Session ID not available');
      }

      console.log('🔔 [LeadAlertCall] Calling accept API:', {
        sessionId: this.sessionId,
        callControlId
      });

      const response = await firstValueFrom(
        this.leadAlertService.acceptLeadAlert(this.sessionId, callControlId)
      );

      console.log('🔔 [LeadAlertCall] Accept response:', response);

      if (response.isWinner) {
        this.callPhase.set('connecting');
        this.statusMessage.set('Connecting to lead...');
        // Modal will close when bridge completes (handled by call state listener)
      } else {
        this.statusMessage.set('Another agent accepted first');
        setTimeout(() => this.dismiss('lost_race'), 2000);
      }
    } catch (error: any) {
      console.error('[LeadAlertCall] Failed to accept lead:', error);
      this.statusMessage.set('Failed to accept - please try again');
      this.callPhase.set('ringing');
    } finally {
      this.isProcessing.set(false);
    }
  }

  /**
   * Decline the lead - call API to notify backend
   */
  async declineLead(): Promise<void> {
    if (this.isProcessing()) return;

    console.log('🔔 [LeadAlertCall] Declining lead');

    this.isProcessing.set(true);
    this.callPhase.set('declined');
    this.statusMessage.set('Declining lead...');

    try {
      const callControlId = this.call?.options?.telnyxCallControlId || '';

      if (!this.sessionId) {
        throw new Error('Session ID not available');
      }

      console.log('🔔 [LeadAlertCall] Calling decline API:', {
        sessionId: this.sessionId,
        callControlId
      });

      await firstValueFrom(
        this.leadAlertService.declineLeadAlert(this.sessionId, callControlId)
      );

      console.log('🔔 [LeadAlertCall] Decline successful');

      this.statusMessage.set('Lead declined');

      // Hang up the call
      await this.calls.endCall('cancelled', 'Lead alert declined');

      // Auto-dismiss after showing decline message
      setTimeout(() => {
        this.dismiss('declined');
      }, 2000);

    } catch (error: any) {
      console.error('[LeadAlertCall] Failed to decline lead:', error);
      this.statusMessage.set('Failed to decline');
      this.callPhase.set('ringing');
    } finally {
      this.isProcessing.set(false);
    }
  }

  /**
   * Get lead initials for avatar
   */
  getInitials(): string {
    const lead = this.leadInfo();
    if (!lead) return '?';

    const name = lead.name || '';
    const names = name.split(' ');

    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    } else if (names.length === 1 && names[0].length > 0) {
      return names[0][0].toUpperCase();
    }

    return '?';
  }

  /**
   * Start ring duration timer
   */
  private startRingTimer(): void {
    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.ringDuration.update(duration => duration + 1);
      });
  }

  /**
   * Start ringtone with proper error handling
   */
  private async startRingtone(): Promise<void> {
    const config = this.ringtoneConfig.getConfig();

    if (!config.enabled) {
      this.showBrowserNotification();
      return;
    }

    try {
      const success = await this.audioService.startRingtone({
        audioFile: config.customUrl || '/assets/audio/ringtone.wav',
        volume: config.volume,
        vibrate: true
      });

      if (!success) {
        this.showBrowserNotification();
      }
    } catch (error) {
      console.error('[LeadAlertCall] Failed to start ringtone:', error);
      this.showBrowserNotification();
    }
  }

  /**
   * Stop ringtone
   */
  private stopRingtone(): void {
    this.audioService.stopRingtone();
  }

  /**
   * Show browser notification as fallback
   */
  private showBrowserNotification(): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      const lead = this.leadInfo();

      const notification = new Notification('Lead Alert', {
        body: `New lead: ${lead?.name || 'Unknown'} (${lead?.phone || 'No phone'})`,
        icon: '/assets/icons/icon-192x192.png',
        tag: 'lead-alert',
        requireInteraction: true
      });

      // Close notification when component is destroyed
      this.destroy$.subscribe(() => {
        notification.close();
      });
    }
  }

  /**
   * Setup call state listener to detect when bridge completes
   */
  private setupCallStateListener(): void {
    // Monitor call state changes for bridge completion
    this.calls.currentState()

    // Monitor call events
    this.telnyxService.getCallEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        console.log('🔔 [LeadAlertCall] Call event:', event);

        // Handle call bridged - transition to GlobalCallPanel
        if (event.event === 'call-bridged' || event.event === 'call-connected' || event.event === 'call-answered') {
          this.handleCallBridged();
        }

        // Handle call ended
        if (['call-ended', 'call-hangup', 'call-cancelled', 'call-failed'].includes(event.event)) {
          this.handleCallEnded();
        }
      });
  }

  /**
   * Handle call bridged - close modal and let GlobalCallPanel take over
   */
  private handleCallBridged(): void {
    if (this.callPhase() === 'connecting' || this.callPhase() === 'accepted') {
      this.statusMessage.set('Connected!');

      // Close modal after short delay
      setTimeout(() => {
        this.dismiss('bridged');
      }, 1000);
    }
  }

  /**
   * Handle call ended - close modal
   */
  private handleCallEnded(): void {
    console.log('🔔 [LeadAlertCall] Call ended');
    this.statusMessage.set('Call ended');
    this.stopRingtone();

    setTimeout(() => {
      this.dismiss('ended');
    }, 2000);
  }

  /**
   * Dismiss the modal
   */
  dismiss(reason: string = 'dismissed'): void {
    console.log('🔔 [LeadAlertCall] Dismissing modal:', reason);
    this.stopRingtone();
    this.dialogRef.close({
      reason,
      leadInfo: this.leadInfo(),
      sessionId: this.sessionId,
      callId: this.call?.id,
      duration: this.ringDuration()
    });
  }

  // Computed properties
  formattedRingDuration = computed(() => {
    const duration = this.ringDuration();
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  });

  getCampusInfo(): string {
    const lead = this.leadInfo();
    return lead?.campusName || 'No campus assigned';
  }

  getProgramInfo(): string {
    const lead = this.leadInfo();
    return lead?.programName || 'No program assigned';
  }

  getOwnerInfo(): string {
    const lead = this.leadInfo();
    return lead?.ownerName || 'No owner assigned';
  }

  getLeadStatus(): string {
    const lead = this.leadInfo();
    return lead?.status || 'Unknown';
  }

  // State check methods
  canAnswerAlert(): boolean {
    return this.callPhase() === 'ringing' && !this.isProcessing();
  }

  canDeclineLead(): boolean {
    return this.callPhase() === 'ringing' && !this.isProcessing();
  }

  isRinging(): boolean {
    return this.callPhase() === 'ringing';
  }

  isAccepted(): boolean {
    return this.callPhase() === 'accepted' || this.callPhase() === 'connecting';
  }

  isDeclined(): boolean {
    return this.callPhase() === 'declined';
  }
}
